import { DropdownEditor, Field } from '@approvalmax/ui';
import { Grid, Tooltip } from '@approvalmax/ui/src/components';
import { numberHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning } from 'modules/request';
import { SectionContainer } from 'pages/requestForm/components';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { memo, useMemo } from 'react';
import { ExchangeRatesRetry } from 'shared/components';

import { isFieldMandatory, isFieldVisible } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../resources/states';
import { NetSuiteExchangeRate } from '../NetSuiteExchangeRate/NetSuiteExchangeRate';
import { NetSuiteExchangeRateDisclaimer } from '../NetSuiteExchangeRateDisclaimer/NetSuiteExchangeRateDisclaimer';
import {
    useCurrencySectionExchangeRates,
    useIsCurrencyFieldDisabled,
    useIsExchangeRateFieldDisabled,
    useTransactionDate,
} from './NetSuiteCurrencySection.hooks';
import { messages } from './NetSuiteCurrencySection.messages';
import { NetSuiteCurrencySectionProps } from './NetSuiteCurrencySection.types';

export const NetSuiteCurrencySection = memo((props: NetSuiteCurrencySectionProps) => {
    const {
        currencies,
        fieldValues,
        fieldSettings,
        expectedCurrencyIsoCode,
        isLoadingCurrencies = false,
        onExchangeRateChange,
        onCurrencyChange,
    } = props;

    const request = useSelector(requestSelector);
    const showErrors = useAtomValue(netSuiteShowErrorsState);

    const isCurrencyFieldDisabled = useIsCurrencyFieldDisabled(
        currencies,
        fieldSettings.currency,
        request,
        fieldValues.transactionNumber
    );

    const isExchangeRateFieldDisabled = useIsExchangeRateFieldDisabled(request, fieldSettings.exchangeRate);

    const { companyCurrency } = request;

    const { currency, exchangeRate } = fieldValues;

    const transactionDate = useTransactionDate(request.integrationCode, fieldValues.date, fieldValues.transactionDate);

    const { exchangeRates, isFetchingExchangeRates, isErrorExchangeRates, refetchExchangeRates } =
        useCurrencySectionExchangeRates(request.companyId, transactionDate, currency);

    const requestCurrency = useMemo(() => {
        if (currency?.id) {
            const selectedCurrency = currencies.find(({ id }) => id === currency?.id);

            return selectedCurrency ?? null;
        }

        return null;
    }, [currencies, currency]);

    const requestCurrencyRate = useMemo<number | null>(() => {
        if (currency?.id) {
            const currencyRate = exchangeRates?.rates.find((rate) => rate.id === currency?.id);

            return currencyRate?.rateValue ?? null;
        }

        return null;
    }, [currency, exchangeRates]);

    const currencyIsoCode = useMemo(
        () =>
            currencies.find(({ id }) => {
                return id === currency?.id;
            })?.ISOCode,
        [currency?.id, currencies]
    );

    const differentCurrency = expectedCurrencyIsoCode !== currencyIsoCode;
    const hasExpectedCurrency = currencies.some(({ ISOCode }) => ISOCode === expectedCurrencyIsoCode);

    const showCurrencyWarning = Boolean(expectedCurrencyIsoCode && (differentCurrency || !hasExpectedCurrency));

    if (!companyCurrency || !(isFieldVisible(fieldSettings.currency) || isFieldVisible(fieldSettings.exchangeRate))) {
        return null;
    }

    return (
        <SectionContainer title={null} noMargin>
            <Grid
                gridTemplateColumns={
                    requestCurrency && companyCurrency !== requestCurrency?.ISOCode ? 'min-content 267px' : '267px'
                }
                rowGap={24}
                columnGap={32}
                alignItems='end'
            >
                {isFieldVisible(fieldSettings.exchangeRate) && (
                    <NetSuiteExchangeRate
                        defaultCurrency={companyCurrency}
                        requestCurrency={requestCurrency?.ISOCode || companyCurrency}
                        exchangeRateValue={exchangeRate}
                        disabled={isExchangeRateFieldDisabled}
                        onExchangeRateValueChange={onExchangeRateChange}
                    />
                )}

                {isFieldVisible(fieldSettings.currency) && (
                    <Field title={messages.currenyFieldText} required={isFieldMandatory(fieldSettings.currency)}>
                        <Tooltip
                            disabled={!showCurrencyWarning}
                            width='max-content'
                            placement='top'
                            activator={
                                <PlainDataProvider items={currencies}>
                                    <DropdownEditor
                                        value={currency || null}
                                        singleLinePerItem={false}
                                        onChange={onCurrencyChange}
                                        placeholder={messages.currenyFieldPlaceholder}
                                        loading={isLoadingCurrencies}
                                        invalid={showErrors && !currency && isFieldMandatory(fieldSettings.currency)}
                                        disabled={isCurrencyFieldDisabled}
                                    />
                                </PlainDataProvider>
                            }
                        >
                            {differentCurrency ? (
                                <TwoDifferentValuesWarning
                                    value1={currencyIsoCode}
                                    value2={expectedCurrencyIsoCode}
                                    label1={messages.differentCurrencyWarningLabel1}
                                    label2={messages.differentCurrencyWarningLabel2}
                                    description={messages.differentCurrencyWarningDescription}
                                />
                            ) : (
                                messages.missingCurrencyWarning
                            )}
                        </Tooltip>
                    </Field>
                )}
            </Grid>

            {companyCurrency !== requestCurrency?.ISOCode && (
                <>
                    <NetSuiteExchangeRateDisclaimer
                        isManualRate={numberHelpers.isNumber(exchangeRate)}
                        date={transactionDate}
                        defaultCurrency={companyCurrency}
                        selectedCurrency={requestCurrency?.ISOCode}
                        exchangeRate={numberHelpers.isNumber(exchangeRate) ? exchangeRate : requestCurrencyRate}
                    />

                    {isErrorExchangeRates && (
                        <ExchangeRatesRetry isLoading={isFetchingExchangeRates} onRetry={refetchExchangeRates} />
                    )}
                </>
            )}
        </SectionContainer>
    );
});

NetSuiteCurrencySection.displayName = 'NetSuiteCurrencySection';
